import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Card,
  CardContent,
  Checkbox,
} from '@mui/material';
import {
  LocalShipping,
  Payment,
  CreditCard,
  AccountBalance,
  ShoppingCart,
  Lock,
} from '@mui/icons-material';
import { fetchCart } from '../../store/slices/cartSlice';
import { RootState, Address, PaymentMethod, CreateOrderRequest } from '../../types';
import { AppDispatch } from '../../store/store';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { DEFAULT_BGN_TO_EUR_RATE, formatPrice as formatPriceForSettings } from '../../utils/currency';
import { useSettings } from '../../contexts/SettingsContext';
import analytics from '../../utils/analytics';

interface CheckoutFormData {
  shippingAddress: Address;
  billingAddress: Address;
  paymentMethod: PaymentMethod;
  sameAsBilling: boolean;
  notes: string;
  guestInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
  };
}

interface ShippingOption {
  type: string;
  name: string;
  price: number;
  deliveryDays: number;
  description: string;
}

const CheckoutPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { items, totalAmount, loading } = useSelector((state: RootState) => state.cart);
  const { currencySettings } = useSettings();
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);

  const steps = [t('checkout.shippingAddress'), t('checkout.paymentMethod'), t('checkout.reviewOrder')];

  const [activeStep, setActiveStep] = useState<number>(0);
  const [processing, setProcessing] = useState<boolean>(false);
  const [formData, setFormData] = useState<CheckoutFormData>({
    shippingAddress: {
      street: '',
      city: '',
      postalCode: '',
      country: 'Bulgaria',
    },
    billingAddress: {
      street: '',
      city: '',
      postalCode: '',
      country: 'Bulgaria',
    },
    paymentMethod: 'CASH_ON_DELIVERY',
    sameAsBilling: true,
    notes: '',
    guestInfo: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phoneNumber: user?.phoneNumber || '',
    },
  });

  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [shippingOptions, setShippingOptions] = useState<ShippingOption[]>([]);
  const [selectedShipping, setSelectedShipping] = useState<ShippingOption | null>(null);
  const [shippingCalculating, setShippingCalculating] = useState<boolean>(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (items.length === 0) {
      dispatch(fetchCart());
    }
  }, [dispatch, isAuthenticated, navigate, items.length]);

  // Update guest info when user data becomes available
  useEffect(() => {
    if (user && isAuthenticated) {
      setFormData(prev => ({
        ...prev,
        guestInfo: {
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          phoneNumber: user.phoneNumber || '',
        }
      }));
    }
  }, [user, isAuthenticated]);

  // Calculate shipping when city changes or total amount changes
  useEffect(() => {
    const calculateShipping = async () => {
      if (!formData.shippingAddress.city.trim()) {
        return;
      }

      setShippingCalculating(true);
      try {
        const totalWeight = items.reduce((sum, item) => sum + (item.quantity * 0.5), 0.5); // Assume 0.5kg per book
        
        const shippingRequest = {
          senderCity: 'Sofia',
          recipientCity: formData.shippingAddress.city,
          weight: Math.max(totalWeight, 0.1),
          packageCount: 1,
          cashOnDelivery: true,
          declaredValue: totalAmount
        };

        const response = await api.post('/shipping/calculate', shippingRequest);
        
        if (response.data.success) {
          setShippingOptions(response.data.shippingOptions);
          // Select the first (cheapest) option by default
          if (response.data.shippingOptions.length > 0) {
            setSelectedShipping(response.data.shippingOptions[0]);
          }
        } else {
          console.error('Shipping calculation failed:', response.data.message);
          // Fallback to default shipping
          setSelectedShipping({ 
            type: 'STANDARD',
            name: 'Стандартна доставка', 
            price: 9.99,
            deliveryDays: 2,
            description: 'Стандартна доставка до адрес'
          });
        }
      } catch (error) {
        console.error('Error calculating shipping:', error);
        // Fallback to default shipping
        setSelectedShipping({ 
          type: 'STANDARD',
          name: 'Стандартна доставка', 
          price: 9.99,
          deliveryDays: 2,
          description: 'Стандартна доставка до адрес'
        });
      } finally {
        setShippingCalculating(false);
      }
    };

    if (formData.shippingAddress.city && totalAmount > 0) {
      calculateShipping();
    }
  }, [formData.shippingAddress.city, totalAmount, items]);

  const validateStep = (step: number): boolean => {
    const errors: { [key: string]: string } = {};

    if (step === 0) {
      // Validate shipping address
      const address = formData.shippingAddress;
      if (!address.street.trim()) errors.shippingStreet = t('checkout.fields.street') + ' ' + t('checkout.messages.requiredField');
      if (!address.city.trim()) errors.shippingCity = t('checkout.fields.city') + ' ' + t('checkout.messages.requiredField');
      if (!address.postalCode.trim()) errors.shippingPostalCode = t('checkout.fields.postalCode') + ' ' + t('checkout.messages.requiredField');

      // Validate billing address if different from shipping
      if (!formData.sameAsBilling) {
        const billing = formData.billingAddress;
        if (!billing.street.trim()) errors.billingStreet = t('checkout.billingAddress') + ' ' + t('checkout.fields.street') + ' ' + t('checkout.messages.requiredField');
        if (!billing.city.trim()) errors.billingCity = t('checkout.billingAddress') + ' ' + t('checkout.fields.city') + ' ' + t('checkout.messages.requiredField');
        if (!billing.postalCode.trim()) errors.billingPostalCode = t('checkout.billingAddress') + ' ' + t('checkout.fields.postalCode') + ' ' + t('checkout.messages.requiredField');
      }
    } else if (step === 1) {
      // Validate payment method
      if (!formData.paymentMethod) {
        errors.paymentMethod = t('checkout.paymentMethod') + ' ' + t('checkout.messages.requiredField');
      }
    } else if (step === 2) {
      // Final validation - check user info
      if (!formData.guestInfo.firstName.trim()) {
        errors.firstName = t('checkout.fields.firstName') + ' ' + t('checkout.messages.requiredField');
      }
      if (!formData.guestInfo.lastName.trim()) {
        errors.lastName = t('checkout.fields.lastName') + ' ' + t('checkout.messages.requiredField');
      }
      if (!formData.guestInfo.email.trim()) {
        errors.email = t('checkout.fields.email') + ' ' + t('checkout.messages.requiredField');
      } else if (!/\S+@\S+\.\S+/.test(formData.guestInfo.email)) {
        errors.email = t('checkout.fields.email') + ' is invalid';
      }
      if (!formData.guestInfo.phoneNumber.trim()) {
        errors.phoneNumber = t('checkout.fields.phone') + ' ' + t('checkout.messages.requiredField');
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleInputChange = (section: 'shippingAddress' | 'billingAddress') => 
    (field: keyof Address) => 
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: e.target.value,
        },
      }));

      // Clear error when user types
      const errorKey = `${section.replace('Address', '')}${field.charAt(0).toUpperCase() + field.slice(1)}`;
      if (formErrors[errorKey]) {
        setFormErrors(prev => ({ ...prev, [errorKey]: '' }));
      }
    };

  const handlePaymentMethodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, paymentMethod: e.target.value as PaymentMethod }));
  };

  const handleSameAsBillingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ 
      ...prev, 
      sameAsBilling: e.target.checked,
      billingAddress: e.target.checked ? prev.shippingAddress : prev.billingAddress,
    }));
  };

  const validateAllFields = (): boolean => {
    const errors: { [key: string]: string } = {};

    // Validate shipping address
    const address = formData.shippingAddress;
    if (!address.street.trim()) errors.shippingStreet = t('checkout.fields.street') + ' ' + t('checkout.messages.requiredField');
    if (!address.city.trim()) errors.shippingCity = t('checkout.fields.city') + ' ' + t('checkout.messages.requiredField');
    if (!address.postalCode.trim()) errors.shippingPostalCode = t('checkout.fields.postalCode') + ' ' + t('checkout.messages.requiredField');

    // Validate billing address if different from shipping
    if (!formData.sameAsBilling) {
      const billing = formData.billingAddress;
      if (!billing.street.trim()) errors.billingStreet = t('checkout.billingAddress') + ' ' + t('checkout.fields.street') + ' ' + t('checkout.messages.requiredField');
      if (!billing.city.trim()) errors.billingCity = t('checkout.billingAddress') + ' ' + t('checkout.fields.city') + ' ' + t('checkout.messages.requiredField');
      if (!billing.postalCode.trim()) errors.billingPostalCode = t('checkout.billingAddress') + ' ' + t('checkout.fields.postalCode') + ' ' + t('checkout.messages.requiredField');
    }

    // Validate payment method
    if (!formData.paymentMethod) {
      errors.paymentMethod = t('checkout.paymentMethod') + ' ' + t('checkout.messages.requiredField');
    }

    // Validate user info (use guestInfo for both authenticated and guest users)
    if (!formData.guestInfo.firstName.trim()) {
      errors.firstName = t('checkout.fields.firstName') + ' ' + t('checkout.messages.requiredField');
    }
    if (!formData.guestInfo.lastName.trim()) {
      errors.lastName = t('checkout.fields.lastName') + ' ' + t('checkout.messages.requiredField');
    }
    if (!formData.guestInfo.email.trim()) {
      errors.email = t('checkout.fields.email') + ' ' + t('checkout.messages.requiredField');
    } else if (!/\S+@\S+\.\S+/.test(formData.guestInfo.email)) {
      errors.email = t('checkout.fields.email') + ' is invalid';
    }
    if (!formData.guestInfo.phoneNumber.trim()) {
      errors.phoneNumber = t('checkout.fields.phone') + ' ' + t('checkout.messages.requiredField');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePlaceOrder = async () => {
    if (!validateAllFields()) return;

    setProcessing(true);
    try {
      const orderData = {
        shipping_address: {
          first_name: formData.guestInfo.firstName,
          last_name: formData.guestInfo.lastName,
          phone: formData.guestInfo.phoneNumber,
          address_line_1: formData.shippingAddress.street,
          city: formData.shippingAddress.city,
          postal_code: formData.shippingAddress.postalCode,
          country: formData.shippingAddress.country,
        },
        billing_address: formData.sameAsBilling ? {
          first_name: formData.guestInfo.firstName,
          last_name: formData.guestInfo.lastName,
          phone: formData.guestInfo.phoneNumber,
          address_line_1: formData.shippingAddress.street,
          city: formData.shippingAddress.city,
          postal_code: formData.shippingAddress.postalCode,
          country: formData.shippingAddress.country,
        } : {
          first_name: formData.guestInfo.firstName,
          last_name: formData.guestInfo.lastName,
          phone: formData.guestInfo.phoneNumber,
          address_line_1: formData.billingAddress.street,
          city: formData.billingAddress.city,
          postal_code: formData.billingAddress.postalCode,
          country: formData.billingAddress.country,
        },
        payment_method: formData.paymentMethod,
        notes: formData.notes,
      };

      const response = await api.post('/orders', orderData);
      const order = response.data.order;

      analytics.purchase(order, items);

      // Refresh cart after successful order creation
      dispatch(fetchCart());

      toast.success(t('checkout.messages.orderPlacedSuccess'));
      navigate(`/orders/${order.order_number}`);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('checkout.messages.orderPlacedError'));
    } finally {
      setProcessing(false);
    }
  };

  if (items.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="info">
          {t('checkout.messages.emptyCart')}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/shop')} sx={{ mt: 2 }}>
          {t('checkout.messages.continueShopping')}
        </Button>
      </Container>
    );
  }

  
  const finalTotal = totalAmount; // No shipping cost calculated here

  // Free shipping over 150 EUR; totalAmount is in BGN, so convert before comparing
  const isFreeShipping = (totalAmount / DEFAULT_BGN_TO_EUR_RATE) >= 150.00;

  const renderShippingForm = () => {
    
    return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('checkout.contactInformation')}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('checkout.fields.firstName')}
            value={formData.guestInfo.firstName}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              guestInfo: { ...prev.guestInfo, firstName: e.target.value }
            }))}
            error={!!formErrors.firstName}
            helperText={formErrors.firstName}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('checkout.fields.lastName')}
            value={formData.guestInfo.lastName}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              guestInfo: { ...prev.guestInfo, lastName: e.target.value }
            }))}
            error={!!formErrors.lastName}
            helperText={formErrors.lastName}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('checkout.fields.email')}
            type="email"
            value={formData.guestInfo.email}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              guestInfo: { ...prev.guestInfo, email: e.target.value }
            }))}
            error={!!formErrors.email}
            helperText={formErrors.email}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('checkout.fields.phone')}
            value={formData.guestInfo.phoneNumber}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              guestInfo: { ...prev.guestInfo, phoneNumber: e.target.value }
            }))}
            error={!!formErrors.phoneNumber}
            helperText={formErrors.phoneNumber}
            required
          />
        </Grid>
      </Grid>
      
      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        {t('checkout.shippingAddress')}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('checkout.fields.street')}
            value={formData.shippingAddress.street}
            onChange={handleInputChange('shippingAddress')('street')}
            error={!!formErrors.shippingStreet}
            helperText={formErrors.shippingStreet}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('checkout.fields.city')}
            value={formData.shippingAddress.city}
            onChange={handleInputChange('shippingAddress')('city')}
            error={!!formErrors.shippingCity}
            helperText={formErrors.shippingCity}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('checkout.fields.postalCode')}
            value={formData.shippingAddress.postalCode}
            onChange={handleInputChange('shippingAddress')('postalCode')}
            error={!!formErrors.shippingPostalCode}
            helperText={formErrors.shippingPostalCode}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.sameAsBilling}
              onChange={handleSameAsBillingChange}
            />
          }
          label={t('checkout.sameAsShipping')}
        />
      </Box>

      {!formData.sameAsBilling && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('checkout.billingAddress')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('checkout.fields.street')}
                value={formData.billingAddress.street}
                onChange={handleInputChange('billingAddress')('street')}
                error={!!formErrors.billingStreet}
                helperText={formErrors.billingStreet}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('checkout.fields.city')}
                value={formData.billingAddress.city}
                onChange={handleInputChange('billingAddress')('city')}
                error={!!formErrors.billingCity}
                helperText={formErrors.billingCity}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('checkout.fields.postalCode')}
                value={formData.billingAddress.postalCode}
                onChange={handleInputChange('billingAddress')('postalCode')}
                error={!!formErrors.billingPostalCode}
                helperText={formErrors.billingPostalCode}
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
  };

  const renderPaymentForm = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('checkout.paymentMethod')}
      </Typography>
      <Card sx={{ border: '2px solid', borderColor: 'primary.main', backgroundColor: 'action.hover' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <LocalShipping sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
            <Box>
              <Typography variant="h6" color="primary">
                {t('checkout.cashOnDelivery')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('checkout.cashOnDeliveryDesc')}
              </Typography>
            </Box>
          </Box>
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">
              {t('checkout.messages.noAdvancePayment')}<br />
              {t('checkout.messages.payOnArrival')}<br />
              {t('checkout.messages.safeConvenient')}
            </Typography>
          </Alert>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            {t('checkout.messages.exactAmountNote')}
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <TextField
          fullWidth
          multiline
          rows={3}
          label={t('checkout.orderNotes')}
          value={formData.notes}
          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
          placeholder={t('checkout.orderNotesPlaceholder')}
        />
      </Box>
    </Box>
  );

  const renderReview = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('checkout.reviewOrder')}
      </Typography>
      
      <Typography variant="subtitle2" gutterBottom>
        {t('checkout.shippingAddress')}:
      </Typography>
      <Typography variant="body2" paragraph>
        {formData.shippingAddress.street}<br />
        {formData.shippingAddress.city} {formData.shippingAddress.postalCode}<br />
        {formData.shippingAddress.country}
      </Typography>

      <Typography variant="subtitle2" gutterBottom>
        {t('checkout.paymentMethod')}:
      </Typography>
      <Typography variant="body2" paragraph>
        {formData.paymentMethod === 'CASH_ON_DELIVERY' ? t('checkout.cashOnDelivery') : formData.paymentMethod.replace('_', ' ')}
      </Typography>

      {formData.notes && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            {t('checkout.orderNotes')}:
          </Typography>
          <Typography variant="body2" paragraph>
            {formData.notes}
          </Typography>
        </>
      )}
    </Box>
  );

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderShippingForm();
      case 1:
        return renderPaymentForm();
      case 2:
        return renderReview();
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('checkout.title')}
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
{/* Validation errors hidden per user request */}
            {renderStepContent(activeStep)}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
              >
                {t('checkout.messages.back')}
              </Button>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handlePlaceOrder}
                  disabled={processing}
                  startIcon={<Lock />}
                >
                  {processing ? t('checkout.messages.processing') : t('checkout.placeOrder')}
                </Button>
              ) : (
                <Button variant="contained" onClick={handleNext}>
                  {t('checkout.messages.next')}
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('checkout.orderSummary')}
            </Typography>
            <List>
              {items.map((item) => (
                <ListItem key={item.id} sx={{ px: 0 }}>
                  <ListItemAvatar>
                    <Avatar
                      src={item.product.imageUrl || '/placeholder-book.svg'}
                      variant="square"
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={item.product.title}
                    secondary={`${t('cart.quantity')}: ${item.quantity}`}
                  />
                  <Typography variant="body2">
                    {formatPriceForSettings(item.product.price ? item.product.price * item.quantity : 0, currencySettings)}
                  </Typography>
                </ListItem>
              ))}
            </List>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography>{t('cart.subtotal')}:</Typography>
              <Typography>{formatPriceForSettings(totalAmount, currencySettings)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography>{t('cart.shipping')}:</Typography>
              <Box sx={{ textAlign: 'right' }}>
                {isFreeShipping ? (
                  <Typography color="success.main">{t('home.features.freeShipping')}</Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('cart.econtTariffs')}
                  </Typography>
                )}
              </Box>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h6">{t('cart.total')}:</Typography>
              <Typography variant="h6" color="primary">
                {formatPriceForSettings(finalTotal, currencySettings)}
              </Typography>
            </Box>

            {isFreeShipping && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {t('checkout.messages.freeShippingQualified')}
              </Alert>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CheckoutPage;